// ═══════════════════════════════════════════════════════════════
// 🎙️ PODCAST CMS — Admin command center for The LCA Podcast
// Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
// Upload an MP3, give it a title, publish — notaries see it instantly.
// Storage: Supabase bucket 'podcast' · Table: podcast_episodes
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const PCMS_STYLES = `
  .pcms-hero { background:linear-gradient(135deg,#0D1B3E,#1A3A5E 60%,#3A2A6E); color:#fff; border-radius:16px; padding:24px 28px; margin-bottom:20px; }
  .pcms-hero h1 { font-size:20px; font-weight:800; margin:0; color:#F8F7F4; }
  .pcms-hero p { font-size:12.5px; color:#C9A84C; margin:4px 0 0; }
  .pcms-card { background:#fff; border:1px solid #E8E6DF; border-radius:12px; padding:18px; margin-bottom:16px; }
  .pcms-label { font-size:11px; font-weight:800; color:#0D1B3E; letter-spacing:0.04em; display:block; margin:12px 0 4px; }
  .pcms-input { width:100%; padding:10px 12px; border:1.5px solid #E0DCCF; border-radius:8px; font-size:13px; font-family:inherit; box-sizing:border-box; }
  .pcms-input:focus { outline:none; border-color:#C9A84C; }
  .pcms-btn { background:#0D1B3E; color:#C9A84C; border:none; border-radius:8px; padding:10px 18px; font-size:13px; font-weight:700; cursor:pointer; margin-top:14px; }
  .pcms-btn:disabled { opacity:0.5; cursor:not-allowed; }
  .pcms-row { display:flex; align-items:center; gap:12px; padding:12px 0; border-bottom:1px solid #F0EEE8; }
  .pcms-row:last-child { border-bottom:none; }
  .pcms-row.drag-over { background:#FDF6E3; box-shadow:inset 0 2px 0 #C9A84C; }
  .pcms-handle { cursor:grab; color:#B8B5AB; font-size:16px; flex-shrink:0; user-select:none; padding:4px 2px; }
  .pcms-handle:active { cursor:grabbing; }
  .pcms-arrows { display:flex; flex-direction:column; gap:2px; flex-shrink:0; }
  .pcms-arrow { border:1px solid #E0DCCF; background:#fff; border-radius:6px; font-size:10px; line-height:1; padding:3px 7px; cursor:pointer; color:#0D1B3E; }
  .pcms-arrow:hover:not(:disabled) { background:#F8F7F4; }
  .pcms-arrow:disabled { opacity:0.3; cursor:default; }
  .pcms-ep { font-size:11px; font-weight:800; color:#9A7A30; flex-shrink:0; width:44px; }
  .pcms-title { font-size:13.5px; font-weight:700; color:#0D1B3E; }
  .pcms-meta { font-size:11px; color:#888780; margin-top:2px; }
  .pcms-pill { font-size:10px; font-weight:800; padding:2px 8px; border-radius:10px; margin-left:8px; }
  .pcms-live { background:#EAF3DE; color:#3B6D11; }
  .pcms-draft { background:#F1EFE8; color:#5F5E5A; }
  .pcms-action { font-size:11.5px; font-weight:700; border:1px solid #E0DCCF; background:#fff; border-radius:8px; padding:5px 10px; cursor:pointer; color:#0D1B3E; }
  .pcms-action.danger { color:#A32D2D; border-color:#F0C4C4; }
  .pcms-action:hover { background:#F8F7F4; }
  .pcms-empty { text-align:center; padding:30px; color:#888780; font-size:13px; }
`

function fmtTime(secs) {
  if (secs == null || isNaN(secs)) return '--:--'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function fmtDateTime(date) {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

// Reads the MP3's duration in the browser before upload
function getAudioDuration(file) {
  return new Promise(resolve => {
    try {
      const url = URL.createObjectURL(file)
      const a = new Audio()
      a.preload = 'metadata'
      a.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(a.duration) || null) }
      a.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
      a.src = url
    } catch (e) { resolve(null) }
  })
}

export default function PodcastCMS() {
  const [episodes, setEpisodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragIdx, setDragIdx] = useState(null)   // index being dragged
  const [overIdx, setOverIdx] = useState(null)   // index being hovered

  // ── 🔀 REORDERING ──────────────────────────────────────────────
  // The list shows newest first, so the TOP row gets the HIGHEST episode
  // number. After any move, numbers are recalculated automatically and
  // only the changed rows are saved.
  function renumber(list) {
    return list.map((ep, i) => ({ ...ep, episode_number: list.length - i }))
  }

  async function persistOrder(newList, oldList) {
    setEpisodes(newList) // optimistic — the UI updates instantly
    try {
      const oldNums = {}
      oldList.forEach(ep => { oldNums[ep.id] = ep.episode_number })
      const changed = newList.filter(ep => oldNums[ep.id] !== ep.episode_number)
      await Promise.all(changed.map(ep =>
        supabase.from('podcast_episodes')
          .update({ episode_number: ep.episode_number })
          .eq('id', ep.id)
      ))
      if (changed.length) toast.success('🔀 Episode order updated!')
    } catch (err) {
      toast.error('Reorder failed: ' + err.message)
      load() // fall back to the database's truth
    }
  }

  function moveRow(from, to) {
    if (to < 0 || to >= episodes.length || from === to) return
    const list = [...episodes]
    const [moved] = list.splice(from, 1)
    list.splice(to, 0, moved)
    persistOrder(renumber(list), episodes)
  }

  function handleDrop(targetIdx) {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return }
    moveRow(dragIdx, targetIdx)
    setDragIdx(null)
    setOverIdx(null)
  }

  async function load() {
    try {
      const { data } = await supabase
        .from('podcast_episodes')
        .select('*')
        .order('episode_number', { ascending: false })
      setEpisodes(data || [])
    } catch (e) { console.warn('[PodcastCMS] load error:', e.message) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function publishEpisode() {
    if (!title.trim()) { toast.error('Give the episode a title!'); return }
    if (!file) { toast.error('Choose an MP3 file to upload!'); return }
    setUploading(true)
    try {
      const duration = await getAudioDuration(file)
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `${Date.now()}-${safeName}`
      const { error: upErr } = await supabase.storage.from('podcast').upload(path, file, {
        contentType: file.type || 'audio/mpeg',
        upsert: false,
      })
      if (upErr) throw upErr
      const { data: pub } = supabase.storage.from('podcast').getPublicUrl(path)
      const nextNumber = episodes.reduce((mx, e) => Math.max(mx, e.episode_number || 0), 0) + 1
      const { error: insErr } = await supabase.from('podcast_episodes').insert({
        episode_number: nextNumber,
        title: title.trim(),
        description: description.trim(),
        audio_url: pub.publicUrl,
        storage_path: path,
        duration_seconds: duration,
        is_published: true,
        published_at: new Date().toISOString(),
      })
      if (insErr) throw insErr
      toast.success(`🎙️ Episode ${nextNumber} is LIVE for the tribe!!`)
      setTitle(''); setDescription(''); setFile(null)
      const input = document.getElementById('pcms-file-input')
      if (input) input.value = ''
      load()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setUploading(false)
    }
  }

  async function togglePublish(ep) {
    try {
      const { error } = await supabase.from('podcast_episodes')
        .update({ is_published: !ep.is_published })
        .eq('id', ep.id)
      if (error) throw error
      toast.success(ep.is_published ? 'Episode unpublished' : '🎉 Episode published!')
      load()
    } catch (err) { toast.error(err.message) }
  }

  async function deleteEpisode(ep) {
    if (!window.confirm(`Delete Episode ${ep.episode_number} — "${ep.title}"? This cannot be undone.`)) return
    try {
      if (ep.storage_path) {
        await supabase.storage.from('podcast').remove([ep.storage_path])
      }
      const { error } = await supabase.from('podcast_episodes').delete().eq('id', ep.id)
      if (error) throw error
      toast.success('Episode deleted')
      load()
    } catch (err) { toast.error(err.message) }
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <style>{PCMS_STYLES}</style>

      <div className="pcms-hero">
        <h1>🎙️ The LCA Podcast — Command Center</h1>
        <p>Upload an episode and it appears in every notary's portal instantly — no deployment needed!!</p>
      </div>

      <div className="pcms-card">
        <div style={{ fontSize: 13, fontWeight: 800, color: '#0D1B3E' }}>📤 PUBLISH A NEW EPISODE</div>
        <label className="pcms-label" htmlFor="pcms-title">EPISODE TITLE</label>
        <input id="pcms-title" className="pcms-input" placeholder="e.g. The Hidden Mechanics of Notarial Acts"
          value={title} onChange={e => setTitle(e.target.value)} />
        <label className="pcms-label" htmlFor="pcms-desc">DESCRIPTION (shown under the title)</label>
        <input id="pcms-desc" className="pcms-input" placeholder="e.g. Companion to the Notarial Acts Done Right course"
          value={description} onChange={e => setDescription(e.target.value)} />
        <label className="pcms-label" htmlFor="pcms-file-input">MP3 FILE</label>
        <input id="pcms-file-input" className="pcms-input" type="file" accept="audio/mpeg,audio/mp3,.mp3"
          onChange={e => setFile(e.target.files?.[0] || null)} />
        <button className="pcms-btn" onClick={publishEpisode} disabled={uploading}>
          {uploading ? '⏳ Uploading episode...' : '🎙️ Publish Episode'}
        </button>
      </div>

      <div className="pcms-card">
        <div style={{ fontSize: 13, fontWeight: 800, color: '#0D1B3E', marginBottom: 4 }}>📻 ALL EPISODES</div>
        {loading ? (
          <div className="pcms-empty">Loading...</div>
        ) : episodes.length === 0 ? (
          <div className="pcms-empty">No episodes yet — publish your first one above!! 🎉</div>
        ) : episodes.map((ep, idx) => (
          <div key={ep.id}
            className={`pcms-row${overIdx === idx && dragIdx !== null && dragIdx !== idx ? ' drag-over' : ''}`}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={e => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx) }}
            onDragLeave={() => { if (overIdx === idx) setOverIdx(null) }}
            onDrop={() => handleDrop(idx)}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}>
            <span className="pcms-handle" title="Drag to reorder">⠿</span>
            <div className="pcms-arrows">
              <button className="pcms-arrow" onClick={() => moveRow(idx, idx - 1)} disabled={idx === 0} aria-label="Move episode up">▲</button>
              <button className="pcms-arrow" onClick={() => moveRow(idx, idx + 1)} disabled={idx === episodes.length - 1} aria-label="Move episode down">▼</button>
            </div>
            <span className="pcms-ep">EP {ep.episode_number}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="pcms-title">{ep.title}</span>
              <span className={`pcms-pill ${ep.is_published ? 'pcms-live' : 'pcms-draft'}`}>
                {ep.is_published ? 'LIVE' : 'HIDDEN'}
              </span>
              <div className="pcms-meta">⏱ {fmtTime(ep.duration_seconds)} · {fmtDateTime(ep.published_at)}</div>
            </div>
            <button className="pcms-action" onClick={() => togglePublish(ep)}>
              {ep.is_published ? 'Unpublish' : 'Publish'}
            </button>
            <button className="pcms-action danger" onClick={() => deleteEpisode(ep)}>Delete</button>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, color: '#888780', textAlign: 'center', marginTop: 4 }}>
        🔀 Drag the ⠿ handle (or use ▲▼) to reorder — episode numbers update automatically, newest at the top.
      </div>
    </div>
  )
}
